import { defineStore } from "pinia"
import { watch } from "vue"
import { RANGE_DATE_LIST } from "~/helpers/date-range.helper"
import { DateModel } from "~/model"
import { Logger } from "~/helpers/logger"

export type DateFilterPeriod = {
    label?: string | null
    value?: string | null
    startDate?: Date | string | null
    endDate?: Date | string | null
}

const STORAGE_KEY = "lolapp.dateFilters"

function emptyPeriod(): DateFilterPeriod {
    return { label: null, value: null, startDate: null, endDate: null }
}

function loadFromStorage(): Record<string, DateFilterPeriod> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return {}
        const parsed = JSON.parse(raw)
        return parsed && typeof parsed === "object" ? parsed : {}
    } catch (e) {
        Logger.error("date-filter", "loadFromStorage", e)
        return {}
    }
}

export const useDateFilterStore = defineStore("dateFilter", {
    state: () => ({
        periods: loadFromStorage() as Record<string, DateFilterPeriod>,
    }),
    getters: {
        getPeriod(state) {
            return (key: string): DateFilterPeriod =>
                state.periods[key] ?? emptyPeriod()
        },
        getFormatted(state) {
            return (key: string) => {
                const p = state.periods[key] ?? emptyPeriod()
                return {
                    startDate: p.startDate
                        ? DateModel.formatApi(p.startDate)
                        : null,
                    endDate: p.endDate ? DateModel.formatApi(p.endDate) : null,
                }
            }
        },
    },
    actions: {
        setPeriod(key: string, period: DateFilterPeriod) {
            if (!key) return
            this.periods = {
                ...this.periods,
                [key]: { ...period },
            }
        },
        ensureDefault(key: string, presetValue: string) {
            if (!key) return
            const current = this.periods[key]
            if (current?.value) return
            const fresh = RANGE_DATE_LIST.find((r) => r.value === presetValue)
            if (fresh) this.setPeriod(key, fresh)
        },
        // Recalcule startDate/endDate depuis RANGE_DATE_LIST au chargement :
        // les dates persistees en localStorage sont figees et deviennent
        // obsoletes (ex: "7 derniers jours" pointe sur une plage passee).
        hydrate() {
            const next: Record<string, DateFilterPeriod> = {}
            for (const [key, period] of Object.entries(this.periods)) {
                if (!period?.value || period.value === "custom") {
                    next[key] = { ...period }
                    continue
                }
                const fresh = RANGE_DATE_LIST.find(
                    (r) => r.value === period.value,
                )
                next[key] = fresh ? { ...fresh } : { ...period }
            }
            this.periods = next
        },
        persist() {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(this.periods))
            } catch (e) {
                Logger.error("date-filter", "persist", e)
            }
        },
    },
})

let persistenceBound = false

export function bindDateFilterPersistence() {
    if (persistenceBound) return
    persistenceBound = true
    const store = useDateFilterStore()
    store.hydrate()
    watch(
        () => store.periods,
        () => store.persist(),
        { deep: true },
    )
}
